import React from 'react';
import DayCell from './DayCell';
import { t } from '../i18n';
import './CalendarGrid.css';

/**
 * CalendarGrid component displays a monthly calendar grid
 * 
 * Requirements:
 * - 5.1: Display a monthly grid with cells for each day
 * 
 * @param {Object} props
 * @param {number} props.year - Year to display
 * @param {number} props.month - Month to display (1-12)
 * @param {Map<string, Object>} props.dailyStats - Map of date (YYYY-MM-DD) to daily stats
 */
function CalendarGrid({ year, month, dailyStats = new Map(), today: serverToday, protectionDays = [], studyDates, onSelectDate, selectedDate, renderDateMenu }) {
  // Calculate calendar grid data
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday
  
  // Get today's date for highlighting
  const today = new Date();
  const todayStr = serverToday || formatDate(today);
  const protectionMap = new Map(protectionDays.map(day => [day.date, day]));
  const studied = Array.isArray(studyDates) ? new Set(studyDates) : null;
  
  // Create array of day cells
  const cells = [];
  
  // Add empty cells for days before month start
  for (let i = 0; i < startDayOfWeek; i++) {
    cells.push(
      <div key={`empty-${i}`} className="day-cell-empty-slot"></div>
    );
  }
  
  // Add cells for each day in the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = formatDate(new Date(year, month - 1, day));
    const stats = dailyStats.get(dateStr) || null;
    const isToday = dateStr === todayStr;
    
    cells.push(
      <div className={`calendar-day-slot${(startDayOfWeek + day - 1) % 7 >= 4 ? ' is-right-side' : ''}`} key={dateStr}>
      <DayCell
        key={dateStr}
        date={dateStr}
        protection={protectionMap.get(dateStr)}
        hasStudied={studied ? studied.has(dateStr) : undefined}
        onSelect={onSelectDate}
        isSelected={selectedDate === dateStr}
        dayNumber={day}
        stats={stats}
        isToday={isToday}
        isCurrentMonth={true}
      />
      {selectedDate === dateStr && renderDateMenu?.(dateStr, protectionMap.get(dateStr), stats)}
      </div>
    );
  }
  
  return (
    <div className="calendar-grid">
      <div className="calendar-grid-header">
        {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
          <div className="day-header" key={day}>{t(`accountActivity.calendar.${day}`)}</div>
        ))}
      </div>
      <div className="calendar-days">
        {cells}
      </div>
    </div>
  );
}

/**
 * Format date as YYYY-MM-DD
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default CalendarGrid;
export { formatDate };
